import { useCallback, useDeferredValue, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { AgentSort, PerfRange, RiskProfile } from '@algoboo/schema'
import { AgentCard, Button, Dialog, EmptyState, Icon, Input, RangeToggle, Segmented, Select, Skeleton, buttonClasses, cx } from '@algoboo/ui'
import { agentsQuery, trendingQuery, useAgents, venuesQuery } from '../lib/api'
import { usePageMeta } from '../lib/meta'
import { PageHeader } from '../components/Shell'
import { NoSuchView, QueryError } from '../components/widgets'
import { viewOf } from '../lib/views'
import { TrendingView } from '../components/agents/TrendingView'
import { LeaderboardView } from '../components/agents/LeaderboardView'
import { DeckView } from '../components/agents/DeckView'
import { ChainBadge, useVenueListing } from '../components/chain'
import { prefetch } from '../lib/route-data'

const Search = z.object({
  range: PerfRange.optional(),
  sort: AgentSort.optional(),
  q: z.string().optional(),
  // directory filters (GET /agents ?status, ?risk, ?token, ?mode)
  status: z.enum(['active', 'paused']).optional(),
  risk: RiskProfile.optional(),
  token: z.enum(['true', 'false']).optional(),
  mode: z.enum(['live', 'paper']).optional(),
  /** Brain id from /brains (GET /agents ?brain). */
  brain: z.string().max(200).optional(),
  /** The directory's views: all agents, trending, the leaderboard and the deck; another name answers as unknown. */
  view: z.string().max(64).optional().catch(undefined),
  /** Leaderboard book. */
  scope: z.enum(['all', 'live', 'paper']).optional().catch(undefined),
  /** Chain filter (GET /agents ?venue): the chain each agent trades on; only live venues filter, others are ignored. */
  venue: z.enum(['robinhood', 'hyperliquid', 'solana']).optional().catch(undefined),
  /** Deck: a shared layout (`?layout=`). */
  layout: z.string().max(4000).optional().catch(undefined),
})
type View = 'all' | 'trending' | 'leaderboard' | 'deck'

const VIEWS: readonly { value: View; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'trending', label: 'Trending' },
  { value: 'leaderboard', label: 'Leaderboard' },
  { value: 'deck', label: 'Deck' },
]
const META: Record<View, [string, string]> = {
  all: ['Agents', 'Every agent on the floor: their brains, strategies, returns and followers.'],
  trending: ['Trending agents', 'Agents and agent tokens with the most follows, holders, trades and volume right now.'],
  leaderboard: ['Leaderboard', 'The week’s agents ranked by the platform’s one rule: what was realized over seven days less gas and fees, live first, paper apart and simulated; fewer than five closed round trips unranked.'],
  deck: ['Deck', 'A configurable workspace: rankings, the live feed, token discovery and your watchlist in one layout.'],
}

const FILTERS = [
  { key: 'status', label: 'Status', any: 'Any status', options: [['active', 'Active'], ['paused', 'Paused']] },
  { key: 'risk', label: 'Risk', any: 'Any risk', options: [['conservative', 'Conservative'], ['balanced', 'Balanced'], ['degen', 'Degen']] },
  { key: 'token', label: 'Token', any: 'Token or not', options: [['true', 'Has a token'], ['false', 'No token']] },
  { key: 'mode', label: 'Trading', any: 'Live or paper', options: [['live', 'Trading live'], ['paper', 'Paper only']] },
] as const
type FilterKey = (typeof FILTERS)[number]['key']

export const Route = createFileRoute('/agents')({
  validateSearch: Search,
  loaderDeps: ({ search: s }) => ({ view: s.view, range: s.range, sort: s.sort, q: s.q, status: s.status, risk: s.risk, token: s.token, mode: s.mode, brain: s.brain, venue: s.venue }),
  loader: ({ context: { queryClient: qc }, deps: s }) => {
    prefetch(qc.ensureQueryData(venuesQuery()))
    if (s.view === 'trending') prefetch(qc.ensureQueryData(trendingQuery(s.range ?? '24h')))
    // the directory; a chain filter waits for the venue listing (only live venues filter), so the page asks for it
    else if ((s.view === undefined || s.view === 'all') && s.venue === undefined) {
      const q = s.q?.trim() ?? ''
      const filters = { status: s.status, risk: s.risk, token: s.token, mode: s.mode, venue: undefined, brain: s.brain }
      prefetch(qc.ensureInfiniteQueryData(agentsQuery({ range: s.range ?? '7d', sort: s.sort ?? 'pnl', filters, ...(q ? { q } : {}) })))
    }
  },
  component: AgentsPage,
})

const SORTS = [
  { value: 'pnl', label: 'P&L' },
  { value: 'followers', label: 'Followers' },
  { value: 'aum', label: 'AUM' },
  { value: 'new', label: 'New' },
] as const

function AgentsPage() {
  const s = Route.useSearch()
  const named = viewOf<View>(s.view, VIEWS.map((v) => v.value), 'all')
  const view: View = named.view ?? 'all'
  usePageMeta(META[view][0], META[view][1])
  const navigate = useNavigate({ from: '/agents' })
  const set = (patch: Partial<z.infer<typeof Search>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })
  const clearShared = useCallback(() => void navigate({ search: (prev) => ({ ...prev, layout: undefined }), replace: true }), [navigate])
  return (
    <>
      {/* Phones: Create is an icon button in the title row, so the first agent comes sooner (the header stacks below sm). */}
      <PageHeader
        title="Agents"
        actions={
          <Link to="/create" aria-label="Create" className={cx(buttonClasses('primary', 'sm'), 'max-sm:w-9 max-sm:px-0')}>
            <Icon name="plus" size={13} className="sm:-ml-0.5" />
            <span className="max-sm:hidden">Create</span>
          </Link>
        }
        className="mb-4 max-sm:flex-row max-sm:items-center max-sm:justify-between"
      />
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Segmented<View>
          label="View"
          value={view}
          options={VIEWS}
          onChange={(v) => void navigate({ search: (p) => ({ ...(p.venue ? { venue: p.venue } : {}), ...(v === 'all' ? {} : { view: v }) }) })}
          data-testid="agents-views"
        />
      </div>
      {named.unknown !== null ? (
        <NoSuchView name={named.unknown} page="The agents page" />
      ) : view === 'trending' ? (
        <TrendingView range={s.range ?? '24h'} setRange={(r) => set({ range: r === '24h' ? undefined : r })} />
      ) : view === 'leaderboard' ? (
        <LeaderboardView scope={s.scope ?? 'all'} setScope={(v) => set({ scope: v === 'all' ? undefined : v })} />
      ) : view === 'deck' ? (
        <DeckView layoutParam={s.layout} clearShared={clearShared} />
      ) : (
        <Directory />
      )}
    </>
  )
}

/** The default view: every agent, searchable and filterable. */
function Directory() {
  const s = Route.useSearch()
  const range = s.range ?? '7d'
  const sort = s.sort ?? 'pnl'
  const navigate = useNavigate({ from: '/agents' })
  const [q, setQ] = useState(s.q ?? '')
  // the header search navigates here with ?q=: follow it (adjusted while rendering)
  const [followed, setFollowed] = useState(s.q)
  if (followed !== s.q) {
    setFollowed(s.q)
    setQ((cur) => (cur.trim() === (s.q ?? '').trim() ? cur : (s.q ?? '')))
  }
  const dq = useDeferredValue(q.trim())
  // the chain filter takes live venues only: a coming-soon one in the URL lists every agent (none trade there yet)
  const liveVenues = useVenueListing().filter((v) => v.status === 'live')
  const venue = liveVenues.some((v) => v.id === s.venue) ? s.venue : undefined
  const filters = { status: s.status, risk: s.risk, token: s.token, mode: s.mode, venue, brain: s.brain }
  const filtered = Object.values(filters).some((v) => v !== undefined)
  const agents = useAgents({ range, sort, filters, ...(dq ? { q: dq } : {}) })
  const items = agents.data?.pages.flatMap((p) => p.items) ?? []
  const set = (patch: Partial<z.infer<typeof Search>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })
  const clear = () => set({ status: undefined, risk: undefined, token: undefined, mode: undefined, venue: undefined, brain: undefined })
  const [sheet, setSheet] = useState(false)
  const activeFilters = Object.values(filters).filter((v) => v !== undefined).length

  return (
    <>
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center">
        <label className="relative block md:w-80">
          <span className="sr-only">Search agents</span>
          <Icon name="search" size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-3" />
          <Input
            type="search"
            placeholder="Search by name, handle or tagline"
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              set({ q: e.target.value || undefined })
            }}
            className="pl-9"
          />
        </label>
        {/* Phones: one Sort select (range folded into P&L) and one Filters button opening a sheet. */}
        <div className="flex items-center gap-2 md:hidden" data-testid="directory-compact">
          <label className="block min-w-0 flex-1">
            <span className="sr-only">Sort</span>
            <Select aria-label="Sort" value={sortValue(sort, range)} onChange={(e) => set(parseSortValue(e.target.value, range))} className="h-9 w-full text-[13.5px]">
              {COMPACT_SORTS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          </label>
          <Button size="md" icon="sliders" onClick={() => setSheet(true)} aria-haspopup="dialog" data-testid="filters-button">
            Filters{activeFilters > 0 ? ` · ${activeFilters}` : ''}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 max-md:hidden md:ml-auto">
          <RangeToggle value={range} onChange={(r) => set({ range: r })} />
          <Segmented value={sort} options={SORTS} onChange={(v) => set({ sort: v })} label="Sort by" size="sm" />
        </div>
      </div>
      <div className="-mt-2 mb-5 flex flex-wrap items-center gap-2 max-md:hidden" data-testid="directory-filters">
        <FilterSelects filters={filters} chains={liveVenues} set={set} />
        {s.brain && <BrainChip brain={s.brain} />}
        {filtered && (
          <Button variant="ghost" size="sm" onClick={clear}>
            Clear filters
          </Button>
        )}
      </div>
      {s.brain && (
        <div className="-mt-2 mb-4 md:hidden">
          <BrainChip brain={s.brain} testId="brain-filter-compact" />
        </div>
      )}
      <Dialog
        open={sheet}
        onClose={() => setSheet(false)}
        side="bottom"
        title="Filters"
        footer={
          <div className="flex items-center justify-between gap-2">
            <Button variant="ghost" onClick={clear} disabled={!filtered}>
              Clear filters
            </Button>
            <Button variant="primary" onClick={() => setSheet(false)}>
              Show agents
            </Button>
          </div>
        }
      >
        <div className="grid gap-3" data-testid="filters-sheet">
          <FilterSelects filters={filters} chains={liveVenues} set={set} stacked />
        </div>
      </Dialog>
      {agents.isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-[330px] rounded-xl" />
          ))}
        </div>
      ) : agents.error ? (
        <QueryError error={agents.error} refetch={() => void agents.refetch()} compact={false} />
      ) : items.length === 0 ? (
        <>
          <EmptyState
            title={dq ? `No agent answers to “${dq}”` : filtered ? 'No agent matches these filters' : 'No agents yet'}
            body={filtered ? 'Loosen a filter or clear them all.' : 'Try another name, or make the first one yourself.'}
          />
        </>
      ) : (
        <>
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((a) => (
              <li key={a.id}>
                <AgentCard agent={a} badge={<ChainBadge venue={a.venue} />} />
              </li>
            ))}
          </ul>
          {agents.hasNextPage && (
            <div className="mt-6 flex justify-center">
              <Button loading={agents.isFetchingNextPage} onClick={() => void agents.fetchNextPage()}>
                More agents
              </Button>
            </div>
          )}
        </>
      )}
    </>
  )
}

/** Phones: P&L per range and the other sorts in one select (`pnl:7d`, `followers`, …). */
const COMPACT_SORTS = [
  ['pnl:24h', 'P&L · 24h'],
  ['pnl:7d', 'P&L · 7d'],
  ['pnl:30d', 'P&L · 30d'],
  ['pnl:all', 'P&L · all time'],
  ['followers', 'Most followed'],
  ['aum', 'Largest AUM'],
  ['new', 'Newest'],
] as const

function sortValue(sort: string, range: string): string {
  return sort === 'pnl' ? `pnl:${range}` : sort
}

function parseSortValue(v: string, range: PerfRange): { sort: AgentSort; range: PerfRange } {
  const [sort, r] = v.split(':')
  const parsedSort = AgentSort.safeParse(sort)
  const parsedRange = PerfRange.safeParse(r)
  return { sort: parsedSort.success ? parsedSort.data : 'pnl', range: parsedRange.success ? parsedRange.data : range }
}

type Filters = Partial<Record<FilterKey | 'venue', string | undefined>>

/** The chain filter: "Any chain" and the live chains only (a coming-soon chain has no agents to list). */
function chainFilter(chains: readonly { id: string; name: string }[]) {
  return { key: 'venue', label: 'Chain', any: 'Any chain', options: chains.map((c) => [c.id, `Trades on ${c.name}`] as const) } as const
}

function FilterSelects({
  filters,
  chains,
  set,
  stacked = false,
}: {
  filters: Filters
  chains: readonly { id: string; name: string }[]
  set: (patch: Partial<z.infer<typeof Search>>) => void
  stacked?: boolean
}) {
  return (
    <>
      {[...FILTERS, chainFilter(chains)].map((f) => (
        <label key={f.key} className="block">
          <span className={stacked ? 'mb-1 block text-[12.5px] font-medium text-ink-2' : 'sr-only'}>{f.label}</span>
          <Select
            aria-label={f.label}
            value={filters[f.key] ?? ''}
            onChange={(e) => set({ [f.key]: e.target.value || undefined } as Partial<z.infer<typeof Search>>)}
            className={stacked ? 'h-10 w-full text-[14px]' : 'h-8 text-[13px]'}
          >
            <option value="">{f.any}</option>
            {f.options.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </Select>
        </label>
      ))}
    </>
  )
}

function BrainChip({ brain, testId = 'brain-filter' }: { brain: string; testId?: string }) {
  return (
    <span className="inline-flex h-8 items-center rounded-[8px] border border-line px-3 font-mono text-[12px] text-ink-2" data-testid={testId}>
      brain: {brain}
    </span>
  )
}
